import { useState, useEffect, useMemo } from 'react';
import { DropDownFilter, useFilterContext, BaseFilterProps } from 'framework/list';
import { Box } from '@mui/material';
import { useShowContext } from 'framework/show';
import { OFFICE_NAMES, useOfficeService } from 'domain/shared/employee';
import { usePerformanceMeasurementService } from 'domain/shared/performanceMeasurement';
import { useAsyncValue } from 'framework/utils';
import { Dropdown, EnumDropdown } from 'framework/form';

interface CompetencyLevelFilterProps extends BaseFilterProps {
  options?: Dictionary<string>;
  label: string;
  labelInDropdown?: string;
  locationFilterSource: string;
  positionSource?: string;
  isVisible?: boolean;
}

const CompetencyLevelFilter = ({
  source,
  locationFilterSource,
  options,
  labelInDropdown,
  positionSource,
  ...props
}: CompetencyLevelFilterProps) => {
  const { filters, clearAndApplyFilter } = useFilterContext();
  const { data } = useShowContext();
  const { findOfficeById } = useOfficeService();
  const { getCompetencyLevelOptions } = usePerformanceMeasurementService();

  const actualSource = Array.isArray(source) ? 'competencyLevel' : source;

  const availableLocationsToQualification = data?.availableLocationsToQualification;
  const availableQualificationToLocation = data?.availableQualificationToLocation;

  const positionFilter = positionSource && filters[positionSource];
  const position = positionFilter ? positionFilter.in?.[0] : null;

  const locationFilter = filters[locationFilterSource];
  const officeId = locationFilter ? locationFilter?.in?.[0] : null;
  const isLocationFilterActive = officeId || false;
  const selectedOffice = useAsyncValue(() => findOfficeById(Number(officeId)), [officeId]);
  const selectedOfficeName = OFFICE_NAMES[selectedOffice?.name || ''];
  const selectedCompetencyLevel = filters[actualSource]?.in?.[0];

  const availableOptions = useMemo(() => {
    if (options) return options;
    return getCompetencyLevelOptions(
      availableLocationsToQualification,
      availableQualificationToLocation,
      selectedOfficeName,
    );
  }, [
    options,
    availableLocationsToQualification,
    availableQualificationToLocation,
    getCompetencyLevelOptions,
    selectedOfficeName,
  ]);

  const [filterStyles, setFilterStyles] = useState({});
  useEffect(() => {
    if (position && selectedCompetencyLevel) {
      clearAndApplyFilter(actualSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  useEffect(() => {
    if (!isLocationFilterActive) {
      setFilterStyles({
        '& .filters-reset-btn': {
          display: 'none',
        },
      });
    } else {
      setFilterStyles({});
    }
  }, [filters, isLocationFilterActive]);

  return (
    <Box sx={filterStyles}>
      <DropDownFilter {...props} source={actualSource} isSingle>
        {Array.isArray(availableOptions) ? (
          <Dropdown source={actualSource} options={availableOptions} />
        ) : (
          <EnumDropdown source={actualSource} options={availableOptions} />
        )}
      </DropDownFilter>
    </Box>
  );
};

export default CompetencyLevelFilter;
